import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import { isCriticalEvent, telemetrySampling } from "@/lib/telemetry-config";
import { shouldSample } from "@/lib/telemetry-sampling";

type PlatformEventCategory = Database["public"]["Enums"]["platform_event_category"];
type PlatformEventStatus = Database["public"]["Enums"]["platform_event_status"];

type PlatformEventLogParams = {
  category: PlatformEventCategory;
  eventType: string;
  status?: PlatformEventStatus;
  userId?: string | null;
  message?: string | null;
  metadata?: Json;
  source?: string | null;
  sampleRate?: number;
};

export async function logPlatformEvent({
  category,
  eventType,
  status,
  userId,
  message,
  metadata,
  source,
  sampleRate,
}: PlatformEventLogParams) {
  // `telemetrySampling.platform.criticalEventTypes` is the legacy
  // stringly-typed array `["auth", "admin_action", "password_reset"]` —
  // those are tier names, not event names. The canonical critical-event
  // registry is `criticalEventTypes` (Record<eventName, config>) at the
  // top of telemetry-config; `isCriticalEvent()` reads from it. Using
  // the legacy array meant security-critical events like
  // `impersonation_started`, `support_mode_entered`,
  // `mfa_challenge_failed` were never marked critical — currently
  // latent because `platform.sampleRate === 1`, but the moment that's
  // lowered, those events start silently dropping.
  const isCritical = isCriticalEvent(eventType);
  const rate = sampleRate ?? telemetrySampling.platform.sampleRate;

  if (!isCritical && !shouldSample(rate)) {
    return;
  }

  await supabase.rpc("log_platform_event", {
    p_category: category,
    p_event_type: eventType,
    p_status: status,
    p_user_id: userId ?? null,
    p_message: message ?? null,
    p_metadata: metadata ?? null,
    p_source: source ?? null,
  });
}
